import { useState } from 'react';

import { readTypedAmount } from '../lib/money.js';
import styles from './MoneyInput.module.css';

/**
 * A VND amount field.
 *
 * `type="text"` with `inputMode="numeric"`, never `type="number"`: the number input brings
 * floating-point semantics and a spinner to a field that must produce an exact integer
 * (skill 12.1). The field owns the digits the user typed and reports the amount upward, so
 * no component ever has to parse money again.
 */
export function MoneyInput({
  id,
  value,
  onChange,
  describedBy,
  autoFocus,
  variant = 'field',
}: {
  id: string;
  value: number | null;
  onChange: (amount: number | null) => void;
  describedBy?: string;
  autoFocus?: boolean;
  /** 'hero' is the big centred amount at the top of Quick Add; 'field' is a normal input. */
  variant?: 'field' | 'hero';
}) {
  // Seeded once from the incoming amount; after that the field is the source of the text and
  // the parent holds the value it reported.
  const [display, setDisplay] = useState(() =>
    value === null ? '' : readTypedAmount(String(value)).display,
  );

  const isHero = variant === 'hero';

  return (
    <div className={isHero ? styles.heroWrapper : styles.wrapper}>
      <input
        id={id}
        className={isHero ? styles.heroInput : styles.input}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        enterKeyHint="done"
        placeholder="0"
        value={display}
        aria-describedby={describedBy}
        autoFocus={autoFocus}
        onChange={(event) => {
          const typed = readTypedAmount(event.target.value);
          setDisplay(typed.display);
          onChange(typed.amount);
        }}
      />
      <span className={isHero ? styles.heroSuffix : styles.suffix} aria-hidden="true">
        {isHero ? 'đ' : '₫'}
      </span>
    </div>
  );
}
